import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { ADD_BLK, LST_BLK, RMV_BLK, RPT } from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import { useAuthStore } from "~/stores/useAuthStore";
import { useHiddenUsersStore } from "~/stores/useHiddenUsersStore";
import type { BlockedUser, ReportRequest } from "./types";

export const blockListQueryOptions = queryOptions({
  queryKey: queryKeys.moderation.blockList(),
  queryFn: () =>
    apiClient<BlockedUser[]>(LST_BLK, {
      user_id: useAuthStore.getState().userId,
      skip: 0,
      take: 100,
    }),
});

type BlockMutationVariables = { userId: string };

// 楽観的更新方針: 失敗時はロールバックのみ（追加のトースト等は出さない）
export const addBlockMutationOptions = mutationOptions({
  mutationFn: (data: BlockMutationVariables) =>
    apiClient<unknown>(ADD_BLK, {
      user_id: useAuthStore.getState().userId,
      req_user_id: data.userId,
    }),
  onSuccess: (_data, variables) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.moderation.blockList(),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.user.profile(variables.userId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.discover.all,
    });
  },
});

export const removeBlockMutationOptions = mutationOptions({
  mutationFn: (data: BlockMutationVariables) =>
    apiClient<unknown>(RMV_BLK, {
      user_id: useAuthStore.getState().userId,
      req_user_id: data.userId,
    }),
  onMutate: async (variables) => {
    await queryClient.cancelQueries({
      queryKey: queryKeys.moderation.blockList(),
    });
    const previous = queryClient.getQueryData<BlockedUser[]>(
      queryKeys.moderation.blockList(),
    );
    queryClient.setQueryData<BlockedUser[]>(
      queryKeys.moderation.blockList(),
      (old) => old?.filter((u) => u.user_id !== variables.userId) ?? [],
    );
    return { previous };
  },
  onError: (_err, _variables, context) => {
    if (context?.previous) {
      queryClient.setQueryData(
        queryKeys.moderation.blockList(),
        context.previous,
      );
    }
  },
  onSettled: (_data, _err, variables) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.moderation.blockList(),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.user.profile(variables.userId),
    });
  },
});

export const reportUserMutationOptions = mutationOptions({
  mutationFn: (data: ReportRequest) =>
    apiClient<unknown>(RPT, {
      rpt_type: 1,
      subject_type: 2,
      subject_id: data.targetUserId,
    }),
  onSuccess: (_data, variables) => {
    useHiddenUsersStore.getState().hide(variables.targetUserId);
  },
});
